/**
 * The call archive: every call in the period, found by the number somebody wrote down or by
 * the booking it became, with the recording beside it.
 *
 * A report like the others — the period is the area's, the export is the same button — and
 * the administrator's like the others, which is the whole of its access rule: recordings are
 * for whoever settles a complaint, not for the desk.
 *
 * The tiles count every call that matched, across every page; the table shows fifty at a
 * time, newest first. The search box takes a number in any form or an order's five-letter
 * reference, and the server works out which. Typing waits for a pause before it asks,
 * because each request is a query over three months of a firm's calls.
 *
 * The player is docked under the table rather than in a row, so that it stays put while
 * somebody scrolls to the next call, and so that stepping through the morning's recordings —
 * the way a complaint is actually investigated — is one key rather than a hunt.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTenantFormats } from '../shell/formats';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Icon,
  Input,
  PageHeader,
  Pill,
  Segmented,
  Select,
  StatTile,
  Sub,
  Switch,
  Table,
  Td,
  Th,
  Tr,
} from '../ui';
import { reportsApi } from './api';
import {
  NO_FILTERS,
  filterQuery,
  isFiltered,
  neighbour,
  otherParty,
  outcomeTone,
  ringSeconds,
  talkSeconds,
  type ArchivedCall,
  type CallFilters,
  type DirectionFilter,
  type OutcomeGroup,
} from './callArchive';
import { ExportButton } from './ExportButton';
import { useReport, type ReportPageProps } from './frame';
import { PeriodRow } from './PeriodRow';
import { RecordingPlayer } from './RecordingPlayer';

/** How long the search box waits after the last key before it asks. */
const TYPING_PAUSE_MS = 350;

export function CallsPage({ frame }: ReportPageProps) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [filters, setFilters] = useState<CallFilters>(NO_FILTERS);
  const [typed, setTyped] = useState('');
  const [page, setPage] = useState(0);

  const [selected, setSelected] = useState<ArchivedCall | null>(null);
  const [start, setStart] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  // The search box is its own state, and reaches the filters after a pause.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) => (current.text === typed ? current : { ...current, text: typed }));
      setPage(0);
    }, TYPING_PAUSE_MS);

    return () => clearTimeout(timer);
  }, [typed]);

  const query = filterQuery(filters, page);

  const { data: archive, reload } = useReport(
    frame,
    (period) => reportsApi.calls(period, query),
    query,
  );

  /** Any change of filter starts again from the first page. */
  const narrow = (change: Partial<CallFilters>) => {
    setFilters((current) => ({ ...current, ...change }));
    setPage(0);
  };

  const calls = useMemo(() => archive?.calls ?? [], [archive]);

  // The player holds its own copy of the row, so that re-reading the archive — after a
  // deletion, or a page turn — refreshes what it shows without taking it off the screen.
  const current = calls.find((call) => call.id === selected?.id) ?? selected;

  const previous = neighbour(calls, current?.id ?? null, -1);
  const next = neighbour(calls, current?.id ?? null, 1);

  const listen = useCallback((call: ArchivedCall) => {
    setSelected(call);
    setStart((count) => count + 1);
  }, []);

  const changed = useCallback(
    (message: string) => {
      setNotice(message);
      reload();
    },
    [reload],
  );

  const totals = archive?.totals;
  const first = archive ? archive.page * archive.pageSize : 0;

  return (
    <>
      <PageHeader
        title={t('nav.reports.calls')}
        description={t('reports.about.calls')}
        actions={
          <ExportButton
            frame={frame}
            path="/api/tenant/reports/calls"
            query={filterQuery(filters)}
          />
        }
      />

      <PeriodRow frame={frame} />

      {notice !== null && (
        <Banner tone="ok">
          <span className="flex items-center justify-between gap-3 w-full">
            {notice}
            <Button size="sm" variant="quiet" onClick={() => setNotice(null)}>
              {t('reports.calls.dismiss')}
            </Button>
          </span>
        </Banner>
      )}

      {totals && (
        <div className="grid grid-cols-5 gap-3">
          <Card>
            <StatTile
              label={t('reports.calls.tiles.calls')}
              value={formats.count(totals.calls)}
              sub={t('reports.calls.tiles.split', {
                inbound: formats.count(totals.inbound),
                outbound: formats.count(totals.calls - totals.inbound),
              })}
            />
          </Card>
          <Card>
            <StatTile
              label={t('reports.calls.tiles.answered')}
              value={formats.percent(totals.inbound > 0 ? totals.answered / totals.inbound : null)}
              sub={t('reports.calls.tiles.answeredOf', {
                answered: formats.count(totals.answered),
                inbound: formats.count(totals.inbound),
              })}
            />
          </Card>
          <Card>
            {/* Red only for the part somebody still owes a call back: the rest is history. */}
            <StatTile
              label={t('reports.calls.tiles.unanswered')}
              value={formats.count(totals.unanswered)}
              tone={totals.stillToCallBack > 0 ? 'danger' : 'neutral'}
              sub={
                totals.stillToCallBack > 0
                  ? t('reports.calls.tiles.toCallBack', { count: totals.stillToCallBack })
                  : t('reports.calls.tiles.allCalledBack')
              }
            />
          </Card>
          <Card>
            <StatTile
              label={t('reports.calls.tiles.ring')}
              value={formats.duration(
                totals.averageAnswerSeconds === null || totals.averageAnswerSeconds === undefined
                  ? null
                  : Math.round(totals.averageAnswerSeconds),
              )}
              sub={t('reports.calls.tiles.ringSub')}
            />
          </Card>
          <Card>
            <StatTile
              label={t('reports.calls.tiles.recorded')}
              value={formats.count(totals.recorded)}
              // Minutes and seconds until there is an hour of it: a firm's twenty-second first
              // recording read as "0:00 of conversation" in hours and minutes.
              sub={
                totals.recordedSeconds < 3600
                  ? t('reports.calls.tiles.recordedSub', {
                      time: formats.duration(totals.recordedSeconds),
                    })
                  : t('reports.calls.tiles.recordedHours', {
                      hours: formats.hours(Math.round(totals.recordedSeconds / 60)),
                    })
              }
            />
          </Card>
        </div>
      )}

      <Card>
        <div className="flex items-center gap-3 flex-wrap px-3.5 py-2.5 border-b border-rule">
          {/* Sized here rather than on the controls: theirs is w-full, and a second width
              utility on the same element is decided by Tailwind's order, not by ours. */}
          <label className="relative flex items-center w-[240px]">
            <span className="absolute left-2.5 text-ink-3 pointer-events-none">
              <Icon name="search" small />
            </span>
            <Input
              type="search"
              className="pl-8"
              value={typed}
              placeholder={t('reports.calls.search')}
              aria-label={t('reports.calls.search')}
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>

          <Segmented<DirectionFilter>
            label={t('reports.calls.direction.label')}
            value={filters.direction}
            onChange={(direction) => narrow({ direction })}
            options={[
              { value: 'all', label: t('reports.calls.direction.all') },
              { value: 'Inbound', label: t('reports.calls.direction.Inbound') },
              { value: 'Outbound', label: t('reports.calls.direction.Outbound') },
            ]}
          />

          <Segmented<OutcomeGroup>
            label={t('reports.calls.outcome.label')}
            value={filters.outcome}
            onChange={(outcome) => narrow({ outcome })}
            options={[
              { value: 'all', label: t('reports.calls.outcome.all') },
              { value: 'answered', label: t('reports.calls.outcome.answered') },
              { value: 'unanswered', label: t('reports.calls.outcome.unanswered') },
              { value: 'other', label: t('reports.calls.outcome.other') },
            ]}
          />

          <div className="w-[190px]">
            <Select
              aria-label={t('reports.calls.dispatcher')}
              value={filters.dispatcher ?? ''}
              onChange={(event) => narrow({ dispatcher: event.target.value || null })}
            >
              <option value="">{t('reports.calls.everyDispatcher')}</option>
              {archive?.dispatchers.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name || t('reports.calls.player.someone')}
                </option>
              ))}
            </Select>
          </div>

          <label className="flex items-center gap-2 text-secondary text-ink-2 cursor-pointer">
            <Switch
              checked={filters.recorded}
              label={t('reports.calls.recordedOnly')}
              onChange={(recorded) => narrow({ recorded })}
            />
            {t('reports.calls.recordedOnly')}
          </label>

          {isFiltered(filters) && (
            <Button
              size="sm"
              variant="quiet"
              onClick={() => {
                setTyped('');
                setFilters(NO_FILTERS);
                setPage(0);
              }}
            >
              {t('reports.calls.clear')}
            </Button>
          )}
        </div>

        {archive !== null && calls.length === 0 ? (
          <EmptyState>
            {isFiltered(filters) ? t('reports.calls.noneMatch') : t('reports.calls.none')}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>{t('reports.calls.columns.when')}</Th>
                  <Th>{t('reports.calls.columns.who')}</Th>
                  <Th>{t('reports.calls.columns.dispatcher')}</Th>
                  <Th>{t('reports.calls.columns.outcome')}</Th>
                  <Th numeric>{t('reports.calls.columns.ring')}</Th>
                  <Th numeric>{t('reports.calls.columns.talk')}</Th>
                  <Th>{t('reports.calls.columns.order')}</Th>
                  <Th>{t('reports.calls.columns.recording')}</Th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call) => (
                  <CallRow
                    key={call.id}
                    call={call}
                    selected={call.id === current?.id}
                    onSelect={() => setSelected(call)}
                    onListen={() => listen(call)}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {archive !== null && archive.total > archive.pageSize && (
          <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-t border-rule">
            <Sub>
              {t('reports.calls.showing', {
                from: formats.count(first + 1),
                to: formats.count(first + calls.length),
                total: formats.count(archive.total),
              })}
            </Sub>
            <div className="flex items-center gap-2">
              <Button size="sm" disabled={page === 0} onClick={() => setPage((at) => at - 1)}>
                {t('reports.calls.newer')}
              </Button>
              <Button
                size="sm"
                disabled={first + calls.length >= archive.total}
                onClick={() => setPage((at) => at + 1)}
              >
                {t('reports.calls.older')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <p className="text-secondary text-ink-3 max-w-[80ch] m-0">{t('reports.calls.footnote')}</p>

      {current && (
        <RecordingPlayer
          call={current}
          start={start}
          previous={previous}
          next={next}
          onStep={listen}
          onChanged={changed}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

/**
 * One call. The row selects it into the player; the play button selects it and plays.
 *
 * The number is shown once: under the customer's name when the firm has one, on its own
 * otherwise. The listening count sits under the play button, because "has anybody heard
 * this already" is asked right before pressing it.
 */
function CallRow({
  call,
  selected,
  onSelect,
  onListen,
}: {
  call: ArchivedCall;
  selected: boolean;
  onSelect: () => void;
  onListen: () => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const navigate = useNavigate();

  const number = otherParty(call);
  const inbound = call.direction === 'Inbound';

  return (
    <Tr selected={selected} onClick={onSelect}>
      <Td>
        <span className="flex items-center gap-2">
          <span
            className={inbound ? 'text-info' : 'text-ink-3'}
            title={t(`reports.calls.direction.${call.direction}`)}
          >
            <Icon name={inbound ? 'arrow-down' : 'arrow-up'} small />
          </span>
          <span className="tabular-nums">{formats.moment(call.startedAt)}</span>
        </span>
      </Td>

      <Td>
        {call.customerName ? (
          <>
            <span className="font-semibold">{call.customerName}</span>
            <Sub block>{number}</Sub>
          </>
        ) : (
          <span className="tabular-nums">{number ?? t('reports.calls.withheld')}</span>
        )}
      </Td>

      <Td>{call.answeredByName ?? <span className="text-ink-3">—</span>}</Td>

      <Td>
        <Pill soft tone={outcomeTone(call)}>
          {t(`reports.calls.outcomes.${call.outcome}`)}
        </Pill>
        {call.needsCallingBack && (
          <Sub block className="text-danger">
            {t('reports.calls.toCallBack')}
          </Sub>
        )}
        {call.handledReason && (
          <Sub block title={call.handledReason}>
            {call.handledReason}
          </Sub>
        )}
      </Td>

      <Td numeric className="tabular-nums">
        {formats.duration(ringSeconds(call))}
      </Td>

      <Td numeric className="tabular-nums">
        {formats.duration(talkSeconds(call))}
      </Td>

      <Td>
        {call.orderReference && call.orderId ? (
          // The booking the call became, one press from its replay — where the recording
          // plays again, in step with the car it sent.
          <Button
            size="sm"
            title={t('reports.calls.replayOrder')}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/reports/journeys?order=${call.orderId}`);
            }}
          >
            <span className="font-mono tracking-[0.04em]">{call.orderReference}</span>
            <Icon name="route" small />
          </Button>
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </Td>

      <Td>
        {call.hasRecording ? (
          <span className="flex items-center gap-2">
            <Button
              icon
              size="sm"
              variant={selected ? 'primary' : 'outline'}
              title={t('reports.calls.player.play')}
              aria-label={t('reports.calls.listenTo', {
                who: call.customerName ?? number ?? t('reports.calls.withheld'),
              })}
              onClick={(event) => {
                event.stopPropagation();
                onListen();
              }}
            >
              <Icon name="play" small />
            </Button>
            <span>
              <span className="tabular-nums">
                {formats.duration(call.recordingSeconds ?? null)}
              </span>
              <Sub block>
                {call.plays === 0
                  ? t('reports.calls.notHeard')
                  : t('reports.calls.heard', { count: call.plays })}
              </Sub>
            </span>
          </span>
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </Td>
    </Tr>
  );
}
